import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   ListSubheader,
   CircularProgress,
   Alert,
   IconButton,
} from '@mui/material';
import {
   Close as CloseIcon,
   Save as SaveIcon,
   People as PeopleIcon,
   Delete as DeleteIcon,
} from '@mui/icons-material';
import {
   RelationshipType,
   relationshipCategories,
   relationshipLabels,
} from '../../types/Relationship';
import { getAuthHeaders } from '../../utils/auth';

interface EditRelationshipDialogProps {
   open: boolean;
   onClose: () => void;
   contact: {
      id: string;
      userId: string;
      email: string;
      displayName: string;
      relationship?: {
         type: RelationshipType;
         label?: string;
         notes?: string;
      };
      // New data structure fields
      relationshipType?: string;
      relationshipLabel?: string;
      notes?: string;
   };
   currentUserId: string;
   onSaved: () => void;
}

const EditRelationshipDialog: React.FC<EditRelationshipDialogProps> = ({
   open,
   onClose,
   contact,
   currentUserId,
   onSaved,
}) => {
   // Helper function to get relationship data from either structure
   const getRelationshipData = () => {
      return {
         type: (contact.relationship?.type || contact.relationshipType) as RelationshipType | '',
         label: contact.relationship?.label || contact.relationshipLabel || '',
         notes: contact.relationship?.notes || contact.notes || '',
      };
   };

   // Initialize state with existing relationship or defaults
   const [relationType, setRelationType] = useState<RelationshipType | ''>('');
   const [relationLabel, setRelationLabel] = useState('');
   const [notes, setNotes] = useState('');
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState(false);
   const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

   // Update state when contact data changes (handles page refresh scenario)
   useEffect(() => {
      if (contact && open) {
         const relationshipData = getRelationshipData();
         setRelationType(relationshipData.type);
         setRelationLabel(relationshipData.label);
         setNotes(relationshipData.notes);
         // Reset error and success states when opening with new data
         setError(null);
         setSuccess(false);
      }
   }, [contact, open]);

   const handleSave = async () => {
      if (!relationType) {
         setError('Please select a relationship type');
         return;
      }

      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
         // Use the contact's userId if available, otherwise use email as identifier
         const contactId = contact.userId || contact.email;

         // Get authentication headers
         const headers = await getAuthHeaders();

         const response = await fetch(`/api/people/contacts/${contactId}/relationship`, {
            method: 'PUT',
            headers: headers,
            body: JSON.stringify({
               userId: currentUserId,
               relationType: relationType,
               relationLabel: relationLabel || null,
               notes: notes || null,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to update relationship');
         }

         // Show success and prepare to close
         setSuccess(true);
         setTimeout(() => {
            onSaved();
            onClose();
         }, 1000);
      } catch (err) {
         console.error('Error updating relationship:', err);
         setError(err instanceof Error ? err.message : 'Failed to update relationship');
      } finally {
         setLoading(false);
      }
   };

   const handleDelete = async () => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setConfirmDeleteOpen(false);

      try {
         // Use the contact's userId if available, otherwise use email as identifier
         const contactId = contact.userId || contact.email;

         // Get authentication headers
         const headers = await getAuthHeaders();

         const response = await fetch(`/api/people/contacts/${contactId}/relationship`, {
            method: 'DELETE',
            headers: headers,
            body: JSON.stringify({
               userId: currentUserId,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to remove contact from network');
         }

         // Show success and prepare to close
         setSuccess(true);
         setTimeout(() => {
            onSaved();
            onClose();
         }, 1000);
      } catch (err) {
         console.error('Error removing contact:', err);
         setError(err instanceof Error ? err.message : 'Failed to remove contact from network');
      } finally {
         setLoading(false);
      }
   };

   return (
      <React.Fragment>
         <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth='sm' fullWidth>
         <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant='h6'>
                  <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Edit Relationship with {contact.displayName}
               </Typography>
               <IconButton edge='end' color='inherit' onClick={onClose} disabled={loading}>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent dividers>
            <Box sx={{ pt: 1 }}>
               <Typography variant='body2' color='text.secondary' gutterBottom>
                  {contact.email}
               </Typography>

               <FormControl fullWidth margin='normal'>
                  <InputLabel id='relationship-type-label'>Relationship Type</InputLabel>
                  <Select
                     labelId='relationship-type-label'
                     value={relationType}
                     label='Relationship Type'
                     onChange={(e) => setRelationType(e.target.value as RelationshipType)}
                  >
                     <MenuItem value=''>
                        <em>None</em>
                     </MenuItem>

                     {Object.entries(relationshipCategories).map(([category, types]) => [
                        <ListSubheader key={category}>{category}</ListSubheader>,
                        ...types.map((type) => (
                           <MenuItem key={type} value={type}>
                              {relationshipLabels[type]}
                           </MenuItem>
                        )),
                     ])}
                  </Select>
               </FormControl>

               <TextField
                  fullWidth
                  margin='normal'
                  label='Custom Label (Optional)'
                  placeholder='e.g. College Roommate, Team Lead'
                  value={relationLabel}
                  onChange={(e) => setRelationLabel(e.target.value)}
                  disabled={!relationType}
               />

               <TextField
                  fullWidth
                  margin='normal'
                  label='Notes (Optional)'
                  placeholder='Add any notes about this relationship'
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
               />

               {error && (
                  <Alert severity='error' sx={{ mt: 2 }}>
                     {error}
                  </Alert>
               )}

               {success && (
                  <Alert severity='success' sx={{ mt: 2 }}>
                     Relationship updated successfully!
                  </Alert>
               )}
            </Box>
         </DialogContent>

         <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
               color='error'
               startIcon={<DeleteIcon />}
               onClick={() => setConfirmDeleteOpen(true)}
               disabled={loading}
            >
               Remove from Network
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
               <Button onClick={onClose} disabled={loading}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  onClick={handleSave}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
               >
                  {loading ? 'Saving...' : 'Save'}
               </Button>
            </Box>
         </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Delete */}
      <Dialog
         open={confirmDeleteOpen}
         onClose={() => setConfirmDeleteOpen(false)}
         maxWidth='xs'
         fullWidth
      >
         <DialogTitle>Remove from Network?</DialogTitle>
         <DialogContent>
            <Typography>
               Are you sure you want to remove <strong>{contact.displayName}</strong> from your
               network? This will delete the relationship and cannot be undone.
            </Typography>
         </DialogContent>
         <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} disabled={loading}>
               Cancel
            </Button>
            <Button
               variant='contained'
               color='error'
               onClick={handleDelete}
               disabled={loading}
               startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
               {loading ? 'Removing...' : 'Remove'}
            </Button>
         </DialogActions>
      </Dialog>
      </React.Fragment>
   );
};

export default EditRelationshipDialog;
